/**
 * 確認トーストの DOM 生成・破棄、送信ペイロードの読み取り（08_client-state-machine.md §6・§8.4）。
 *
 * 表示位置（unify-study-toast-stacking design.md D2）:
 *   専用の位置決めラッパー（.lms-study-toast-host / top: 56px）は廃止し、ピル本体を
 *   汎用トーストと同じ共有スタック（.lms-toast-host）へ到着順に appendChild する。
 *   これにより「他のトーストがあれば学習中はその下」「学習中の表示中に出た他トーストは
 *   学習中の下」の両方が DOM 挿入順だけで満たされる。
 *   読み上げは従来どおりピル自身の ARIA 属性（role="dialog" / aria-live="assertive"）で行い、
 *   汎用トースト用のアナウンサー（[data-lms-toast-announcer]）は使わない。
 */
import { ensureToastStack } from "../ui/toast.js";
import { TOAST_MESSAGE, type LearningLogPayload } from "./state.js";

/**
 * バースト退場アニメーション（lmsBurst）の再生時間。CSS の --dur-study-burst と一致させる。
 * この時間だけ待ってから DOM を除去することで、弾けて消える演出を最後まで見せる。
 */
const BURST_DURATION_MS = 450;

/**
 * バースト退場中にピルを除去する予約タイマー。退場アニメ再生中のみ非 null。
 * 二重発火の抑止と、退場中の再表示要求（ページ往復）でバーストを取り消すために保持する。
 */
let leaveTimer: number | null = null;

/** 状態に合わせて確認トーストの DOM を同期する。すでに一致していれば何もしない。 */
export function syncToast(visible: boolean, onConfirm: () => void): void {
  const existing = document.querySelector<HTMLElement>("[data-lms-study-toast]");

  if (visible) {
    if (existing !== null) {
      // 退場アニメ再生中に再表示要求が来たらバーストを取り消し、定位置に戻す
      // （部分更新でのページ往復などでのちらつきを防ぐ。§6.3）。
      if (leaveTimer !== null) {
        // window.setTimeout と対で解決先を揃える（jsdom テストで素の clearTimeout と混線しないため）
        window.clearTimeout(leaveTimer);
        leaveTimer = null;
        existing.classList.remove("is-leaving");
      }
      // 既に表示中なら再生成しない（ドロップインの再生を防ぐ。§6.1 手順 3）。
      return;
    }

    // ピル本体がダイアログの実体。照会・除去・退場アニメの単位もこの要素に統一する。
    const pill = document.createElement("div");
    pill.className = "lms-study-toast";
    pill.setAttribute("data-lms-study-toast", "");
    pill.setAttribute("role", "dialog");
    pill.setAttribute("aria-live", "assertive");
    pill.setAttribute("aria-labelledby", "lms-study-toast-title");
    // data-lms-modal は付けない（ui/modal.ts の initModalKeyboard が ESC で閉じてしまうため。Scenario S6）。
    // keydown / keyup / document の click リスナも一切登録しない。オーバーレイも作らない。

    const title = document.createElement("p");
    title.className = "lms-study-toast__title";
    title.id = "lms-study-toast-title";
    title.textContent = TOAST_MESSAGE;
    pill.appendChild(title);

    const confirmButton = document.createElement("button");
    confirmButton.type = "button";
    confirmButton.className = "lms-btn lms-btn--primary";
    confirmButton.setAttribute("data-lms-study-toast-confirm", "");
    confirmButton.textContent = "はい";
    // 生成時にフォーカスは移さない（05_ui.md §3.3）。
    confirmButton.addEventListener("click", onConfirm);
    pill.appendChild(confirmButton);

    // 汎用トーストと同じ共有スタックの末尾へ積む（＝表示中の他トーストの下に出る）。
    ensureToastStack().appendChild(pill);
    return;
  }

  // visible === false
  if (existing === null) return; // 既に無い。
  if (leaveTimer !== null) return; // 既にバースト退場中（二重発火させない）。

  // バースト退場を再生し、再生後に DOM から除去する。
  existing.classList.add("is-leaving");
  leaveTimer = window.setTimeout(() => {
    existing.remove();
    leaveTimer = null;
  }, BURST_DURATION_MS);
}

/** 送信ペイロードを DOM から読む。curriculumId が取れなければ null。 */
export function readPayload(): LearningLogPayload | null {
  const mount = document.querySelector<HTMLElement>("[data-lms-learning-log]");
  if (mount === null) return null;
  const curriculumId = (mount.dataset["lmsCurriculumId"] ?? "").trim();
  if (curriculumId === "") return null;
  return { curriculumId, activeHeading: readActiveHeading() };
}

/**
 * アクティブな目次見出しの id を読む（curriculum-content 依存。README §4）。
 *
 * data-lms-toc-link 属性の値そのものが見出しの id（h-1 形式）である。data-lms-heading-id という属性は
 * 存在しない。
 */
function readActiveHeading(): string | null {
  const active = document.querySelector<HTMLElement>("[data-lms-toc-link].is-active");
  if (active === null) return null;
  const id = (active.dataset["lmsTocLink"] ?? "").trim();
  if (id === "") return null;
  return id;
}
